// State -> colour tone, one entry per enum member of the contract plus the
// three chapter labels the screens add. Adding a state to
// src/contract/states.ts or to CHAPTER_DISPLAY_STATES without a row here is a
// type error, and tests/client/components/stateChip.test.tsx proves every row
// has a string in both catalogues.
import type { BookState, ChapterState, JobStatus } from "@contract";
import type { ChapterDisplayState } from "../../../i18n/resources";

export type StateTone = "muted" | "neutral" | "working" | "accent" | "ok" | "warn" | "danger";

export const BOOK_STATE_TONE: Readonly<Record<BookState, StateTone>> = Object.freeze({
  uploaded: "neutral",
  parsed: "neutral",
  confirmed: "neutral",
  styleSheetReady: "neutral",
  calibrating: "working",
  refining: "working",
  inReview: "accent",
  approved: "ok",
  rendering: "working",
  rendered: "ok",
  failed: "danger",
});

export const CHAPTER_STATE_TONE: Readonly<Record<ChapterState, StateTone>> = Object.freeze({
  pending: "neutral",
  refining: "working",
  inReview: "accent",
  approved: "ok",
  rendering: "working",
  rendered: "ok",
  excluded: "muted",
  failed: "danger",
});

export const JOB_STATE_TONE: Readonly<Record<JobStatus, StateTone>> = Object.freeze({
  queued: "neutral",
  running: "working",
  succeeded: "ok",
  failed: "danger",
  cancelled: "muted",
});

/**
 * Labels a chapter row shows that are not ChapterStates: a render waiting for
 * its turn, Chapter Audio superseded by an edit, a render the Author stopped.
 * The catalogue carries them under states:chapter.* next to the contract
 * states, so one StateChip covers a row.
 */
export const CHAPTER_DISPLAY_STATE_TONE: Readonly<Record<ChapterDisplayState, StateTone>> =
  Object.freeze({
    queued: "neutral",
    stale: "warn",
    cancelled: "muted",
  });

export type StateKind = "book" | "chapter" | "job";

export type StateOf<K extends StateKind> = K extends "book"
  ? BookState
  : K extends "chapter"
    ? ChapterState | ChapterDisplayState
    : JobStatus;

export function toneOf<K extends StateKind>(kind: K, state: StateOf<K>): StateTone {
  switch (kind) {
    case "book":
      return BOOK_STATE_TONE[state as BookState];
    case "chapter":
      return (
        CHAPTER_STATE_TONE[state as ChapterState] ??
        CHAPTER_DISPLAY_STATE_TONE[state as ChapterDisplayState]
      );
    default:
      return JOB_STATE_TONE[state as JobStatus];
  }
}
